import { supabase } from '../../supabase';
import { getCachedGymId } from './gymContext';
import { loadAdminSettings, saveAdminSettings } from '../../pages/admin/settings/settingsData';
import { isOnline } from '../offline/network';
import { isOfflineInvoiceId, nextOfflineInvoiceId } from '../offline/ids';

/**
 * Default next CR. No. / Invoice ID — first digital receipt uses paper book CR 36128.
 * Next allocate returns at least this value when DB/local max are lower.
 */
export const INVOICE_SEQUENCE_START = 36128;

/** Brief interim floor (paper 36128 already used → start 36129); migrate back to 36128. */
const PREVIOUS_PAPER_FLOOR = 36129;

/** Legacy default before paper-book alignment — migrate local floors still on this. */
const LEGACY_INVOICE_SEQUENCE_START = 100001;

/** Serialize allocate calls in this browser tab (prevents same-tab races). */
let allocateChain = Promise.resolve();

function counterKey(gymId) {
  return `gym:${gymId || 'default'}:invoice-sequence`;
}

function floorKey(gymId) {
  return `gym:${gymId || 'default'}:invoice-sequence-floor`;
}

function readLocalMax(gymId) {
  if (typeof window === 'undefined') return 0;
  try {
    const n = Number(localStorage.getItem(counterKey(gymId)));
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
}

function writeLocalMax(gymId, value) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(counterKey(gymId), String(Math.max(0, Number(value) || 0)));
  } catch {
    /* ignore quota */
  }
}

function readSettingsFloor(gymId) {
  if (typeof window === 'undefined') return 0;
  try {
    const settings = loadAdminSettings(gymId);
    const n = Number(settings?.invoiceSequenceStart);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  } catch {
    /* ignore */
  }
  return 0;
}

function writeSettingsFloor(gymId, n) {
  if (typeof window === 'undefined') return;
  try {
    const settings = loadAdminSettings(gymId);
    if (Number(settings.invoiceSequenceStart) === n) {
      // Still refresh localStorage key below
    } else {
      saveAdminSettings({ ...settings, invoiceSequenceStart: n }, gymId);
    }
  } catch {
    /* ignore */
  }
}

function readLocalFloorRaw(gymId) {
  if (typeof window === 'undefined') return 0;
  try {
    const n = Number(localStorage.getItem(floorKey(gymId)));
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  } catch {
    /* ignore */
  }
  return 0;
}

function writeLocalFloor(gymId, n) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(floorKey(gymId), String(n));
  } catch {
    /* ignore */
  }
}

function resetLocalMaxForPaperFloor(gymId, floor) {
  const lm = readLocalMax(gymId);
  // Drop legacy 100001+ counters; also rewind any test bump at/above the new floor
  if (lm >= LEGACY_INVOICE_SEQUENCE_START) {
    writeLocalMax(gymId, Math.max(floor - 1, 0));
  } else if (lm >= floor) {
    writeLocalMax(gymId, floor - 1);
  } else {
    writeLocalMax(gymId, Math.max(lm, floor - 1));
  }
}

function readSequenceFloor(gymId) {
  let settingsFloor = readSettingsFloor(gymId);
  let localFloor = readLocalFloorRaw(gymId);

  // Migrate interim 36129 floor → 36128 (no paper-series invoices issued yet)
  if (settingsFloor === PREVIOUS_PAPER_FLOOR || localFloor === PREVIOUS_PAPER_FLOOR) {
    writeLocalFloor(gymId, INVOICE_SEQUENCE_START);
    writeSettingsFloor(gymId, INVOICE_SEQUENCE_START);
    resetLocalMaxForPaperFloor(gymId, INVOICE_SEQUENCE_START);
    settingsFloor = INVOICE_SEQUENCE_START;
    localFloor = INVOICE_SEQUENCE_START;
  }

  if (settingsFloor > 0) {
    // Keep localStorage in sync for allocate speed / offline
    if (readLocalFloorRaw(gymId) !== settingsFloor) writeLocalFloor(gymId, settingsFloor);
    const lm = readLocalMax(gymId);
    // Clear local counter stuck in old 100001+ series when using paper CR floor
    if (settingsFloor < LEGACY_INVOICE_SEQUENCE_START && lm >= LEGACY_INVOICE_SEQUENCE_START) {
      writeLocalMax(gymId, Math.max(settingsFloor - 1, 0));
    }
    return settingsFloor;
  }

  // Migrate browsers still on the old 100001 floor to the paper-book series
  if (localFloor === LEGACY_INVOICE_SEQUENCE_START) {
    writeLocalFloor(gymId, INVOICE_SEQUENCE_START);
    writeSettingsFloor(gymId, INVOICE_SEQUENCE_START);
    resetLocalMaxForPaperFloor(gymId, INVOICE_SEQUENCE_START);
    return INVOICE_SEQUENCE_START;
  }

  if (localFloor > 0) {
    writeSettingsFloor(gymId, localFloor);
    return localFloor;
  }

  // First visit: seed settings + localStorage to paper CR 36128
  writeLocalFloor(gymId, INVOICE_SEQUENCE_START);
  writeSettingsFloor(gymId, INVOICE_SEQUENCE_START);
  resetLocalMaxForPaperFloor(gymId, INVOICE_SEQUENCE_START);
  return INVOICE_SEQUENCE_START;
}

/**
 * Seed the next CR. No. / invoice number (call when client provides start N).
 * Next allocateNextInvoiceId() returns at least this value.
 */
export function setInvoiceSequenceStart(nextNumber, explicitGymId = '') {
  const gid = explicitGymId || getCachedGymId() || 'default';
  const n = Math.floor(Number(nextNumber) || 0);
  if (!Number.isFinite(n) || n < 1) {
    throw new Error('Enter a valid starting CR. No. (1 or higher).');
  }
  writeLocalFloor(gid, n);
  writeSettingsFloor(gid, n);
  // Next allocate returns `n` — rewind past legacy / test counters
  resetLocalMaxForPaperFloor(gid, n);
  return String(n);
}

export function getInvoiceSequenceStart(explicitGymId = '') {
  const gid = explicitGymId || getCachedGymId() || 'default';
  return readSequenceFloor(gid);
}

/** True sequential invoice / CR ids are digits only. */
export function parseGymInvoiceNumber(value) {
  const raw = String(value || '').trim();
  if (!/^\d+$/.test(raw)) return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function formatGymInvoiceId(n) {
  const floor = typeof window !== 'undefined'
    ? readSequenceFloor(getCachedGymId() || 'default')
    : INVOICE_SEQUENCE_START;
  const num = Math.max(floor, Math.floor(Number(n) || floor));
  return String(num);
}

function isRpcMissing(error) {
  const msg = String(error?.message || error || '').toLowerCase();
  const code = String(error?.code || '').toLowerCase();
  return (
    code === 'pgrst202'
    || (msg.includes('could not find the function') && msg.includes('allocate_next_invoice_id'))
    || (msg.includes('allocate_next_invoice_id') && msg.includes('does not exist'))
    || (msg.includes('schema cache') && msg.includes('allocate_next_invoice_id'))
  );
}

/**
 * Max numeric invoice_id for this gym.
 * When paper CR floor is active (< 100001), ignore legacy digital IDs (100001+)
 * so sequence continues 36128, 36129… instead of jumping to 100276.
 */
async function fetchDbMaxInvoiceNumber(gid, { paperSeries = false } = {}) {
  if (!gid) return 0;
  // Page through invoice_ids — a single unordered LIMIT 5000 missed the true max.
  let max = 0;
  let from = 0;
  const pageSize = 1000;
  for (;;) {
    const { data, error } = await supabase
      .from('payments')
      .select('invoice_id')
      .eq('gym_id', gid)
      .not('invoice_id', 'is', null)
      .range(from, from + pageSize - 1);
    if (error) break;
    const rows = data || [];
    for (const row of rows) {
      const n = parseGymInvoiceNumber(row.invoice_id);
      if (!n) continue;
      if (paperSeries && n >= LEGACY_INVOICE_SEQUENCE_START) continue;
      if (n > max) max = n;
    }
    if (rows.length < pageSize) break;
    from += pageSize;
    if (from > 100000) break;
  }
  return max;
}

async function allocateViaRpc(gid, floor) {
  const { data, error } = await supabase.rpc('allocate_next_invoice_id', {
    p_gym_id: gid,
    p_floor: floor,
  });
  if (error) {
    if (isRpcMissing(error)) return null;
    throw error;
  }
  const parsed = parseGymInvoiceNumber(data);
  return parsed ? String(parsed) : null;
}

/**
 * Next CR. No. / invoice id for this gym across all sale types.
 * Prefers atomic DB RPC (required online) so multi-desk sales never share a Rec NO.
 */
export async function allocateNextInvoiceId(explicitGymId = '') {
  const run = async () => {
    if (!isOnline()) {
      return nextOfflineInvoiceId();
    }
    const gid = explicitGymId || getCachedGymId() || 'default';
    const floor = readSequenceFloor(gid);
    if (gid && gid !== 'default') {
      try {
        const fromRpc = await allocateViaRpc(gid, floor);
        if (fromRpc) {
          const n = parseGymInvoiceNumber(fromRpc);
          if (n > readLocalMax(gid)) writeLocalMax(gid, n);
          return fromRpc;
        }
      } catch (err) {
        if (!isRpcMissing(err)) {
          // Still try fallback below, but surface the failure for support
          console.warn('allocate_next_invoice_id RPC failed, using fallback', err?.message || err);
        }
      }
    }

    const paperSeries = floor > 0 && floor < LEGACY_INVOICE_SEQUENCE_START;
    const dbMax = await fetchDbMaxInvoiceNumber(gid === 'default' ? '' : gid, { paperSeries });
    let localMax = readLocalMax(gid);
    if (paperSeries && localMax >= LEGACY_INVOICE_SEQUENCE_START) {
      localMax = Math.max(floor - 1, 0);
      writeLocalMax(gid, localMax);
    }
    // Never rewind localMax to dbMax — that reused in-flight Rec NOs under load.
    const next = Math.max(floor - 1, dbMax, localMax) + 1;
    writeLocalMax(gid, next);
    return String(next);
  };

  const nextPromise = allocateChain.then(run, run);
  allocateChain = nextPromise.then(() => undefined, () => undefined);
  return nextPromise;
}

/**
 * Resolve invoice id for a payment insert.
 * Online: leave blank so DB BEFORE INSERT trigger assigns Rec NO in the same
 * transaction — failed inserts do not burn numbers / create gaps.
 * Offline: use OFF- placeholder until sync.
 */
export async function resolveInvoiceId(existing, explicitGymId = '') {
  if (!isOnline()) {
    if (isOfflineInvoiceId(existing)) return String(existing);
    const parsedOffline = parseGymInvoiceNumber(existing);
    if (parsedOffline) return String(parsedOffline);
    return nextOfflineInvoiceId();
  }

  // Online offline-placeholder → clear; trigger will assign on INSERT
  if (isOfflineInvoiceId(existing) || existing == null || String(existing).trim() === '') {
    return '';
  }

  const parsed = parseGymInvoiceNumber(existing);
  if (parsed) {
    const gid = explicitGymId || getCachedGymId() || 'default';
    const floor = readSequenceFloor(gid);
    const paperSeries = floor > 0 && floor < LEGACY_INVOICE_SEQUENCE_START;
    // Don't keep a reused/stale legacy id (100001+) on paper CR series
    if (paperSeries && parsed >= LEGACY_INVOICE_SEQUENCE_START) {
      return '';
    }
    const already = await invoiceIdAlreadyUsed(String(parsed), gid === 'default' ? '' : gid);
    if (already) return '';
    const localMax = readLocalMax(gid);
    if (parsed > localMax) writeLocalMax(gid, parsed);
    return String(parsed);
  }

  return '';
}

/** True when this gym already has a payment with this invoice / Rec NO. */
export async function invoiceIdAlreadyUsed(invoiceId, gymId, excludePaymentId = '') {
  const id = String(invoiceId || '').trim();
  const gid = gymId || getCachedGymId();
  if (!id || !gid) return false;
  let q = supabase
    .from('payments')
    .select('id')
    .eq('gym_id', gid)
    .eq('invoice_id', id)
    .limit(1);
  if (excludePaymentId) q = q.neq('id', excludePaymentId);
  const { data, error } = await q;
  if (error) return false;
  return Array.isArray(data) ? data.length > 0 : Boolean(data?.id);
}

/**
 * Allocate a Rec NO that is not already on another payment row.
 * Retries a few times if a race still collides.
 */
export async function allocateUniqueInvoiceId(explicitGymId = '', attempts = 8) {
  const gid = explicitGymId || getCachedGymId() || 'default';
  for (let i = 0; i < attempts; i += 1) {
    const next = await allocateNextInvoiceId(gid);
    if (isOfflineInvoiceId(next)) return next;
    // eslint-disable-next-line no-await-in-loop
    const used = await invoiceIdAlreadyUsed(next, gid === 'default' ? '' : gid);
    if (!used) return next;
    // Collision: bump local max past the taken number and retry
    const n = parseGymInvoiceNumber(next);
    if (n > readLocalMax(gid)) writeLocalMax(gid, n);
  }
  return allocateNextInvoiceId(gid);
}
